// 내 프로필 페이지
// - 사용자 정보 조회/수정, 근무 상태 업데이트(/manager-status), 소속 변경 권한 가드
// - UI는 한국어 라벨과 공통 날짜/라벨 유틸 기준에 맞춤
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { Roles, RoleLabels } from '../auth/rbac'
import { api } from '../lib/api'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'

export default function ProfilePage() {
    const { logout, user: currentUser } = useAuth()
    const { show } = useToast()
    const qc = useQueryClient()
    const meQuery = useQuery({
        queryKey: ['me'],
        queryFn: async () => {
            const res = await api.get('/users/me')
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        }
    })

    const [name, setName] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [status, setStatus] = useState('ACTIVE') // 계정 상태 (표시용)
    const [availability, setAvailability] = useState('ON_DUTY') // 근무 상태
    const [orgId, setOrgId] = useState('')
    const [orgOptions, setOrgOptions] = useState([])
    const [originalOrgId, setOriginalOrgId] = useState('')
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [nextOrgId, setNextOrgId] = useState('')
    const [notifyLoading, setNotifyLoading] = useState(false)
    const [notifyMessage, setNotifyMessage] = useState('')

    // 비밀번호 변경(팝업)
    const [pwOpen, setPwOpen] = useState(false)
    const [pwCurrent, setPwCurrent] = useState('')
    const [pwNext, setPwNext] = useState('')
    const [pwNextConfirm, setPwNextConfirm] = useState('')

    // 권한 가드: 지점 관리자 이상만 소속 변경 가능
    const canChangeOrg = useMemo(() => {
        const roles = currentUser?.roles || []
        return roles.includes(Roles.OPERATOR) || roles.includes(Roles.GA_LEADER) || roles.includes(Roles.INSURER_LEADER)
    }, [currentUser])

    const canTestNotification = useMemo(() => {
        const roles = currentUser?.roles || []
        if (!Array.isArray(roles)) return false
        return roles.includes(Roles.OPERATOR)
            // || roles.includes(Roles.GA_LEADER)
            // || roles.includes(Roles.GA_BRANCH_MANAGER)
            // || roles.includes(Roles.GA_DEPUTY_BRANCH_MANAGER)
            // || roles.includes(Roles.INSURER_LEADER)
            // || roles.includes(Roles.INSURER_BRANCH_MANAGER)
            // || roles.includes(Roles.INSURER_DEPUTY_BRANCH_MANAGER)
    }, [currentUser])

    const isGaPlanner = useMemo(() => {
        const roles = currentUser?.roles || []
        return Array.isArray(roles) && roles.includes(Roles.GA_PLANNER)
    }, [currentUser])

    // 근무 상태 기간/대직자
    const [availStart] = useState('')
    const [availEnd] = useState('')
    const [deputyCandidateOptions, setDeputyCandidateOptions] = useState([])
    const [deputyUserId1, setDeputyUserId1] = useState('')
    const [deputyUserId2, setDeputyUserId2] = useState('')

    React.useEffect(() => {
        if (meQuery.data) {
            setName(meQuery.data.name || '')
            setPhoneNumber(meQuery.data.phoneNumber || '')
            setStatus(meQuery.data.status || 'ACTIVE')
            setAvailability(meQuery.data.availabilityStatus || 'ON_DUTY')
            const currentOrgId = String(meQuery.data.orgId || '')
            setOrgId(currentOrgId)
            setOriginalOrgId(currentOrgId)
        }
    }, [meQuery.data])

    React.useEffect(() => {
        let ignore = false
        async function loadOrgs() {
            try {
                const res = await api.get('/orgs/options')
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (!ignore) setOrgOptions(Array.isArray(payload) ? payload : [])
            } catch { }
        }
        loadOrgs()
        return () => { ignore = true }
    }, [])

    // 동일 조직 대직자 후보 로딩 (선택된 org 기준)
    React.useEffect(() => {
        let ignore = false
        async function loadDeputies() {
            try {
                if (isGaPlanner) {
                    if (!ignore) setDeputyCandidateOptions([])
                    return
                }
                if (!orgId) {
                    if (!ignore) setDeputyCandidateOptions([])
                    return
                }
                const params = { orgId: Number(orgId), size: 100 }
                const res = await api.get('/users', { params })
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                const list = Array.isArray(payload) ? payload : (payload?.content || payload?.items || [])
                const mapped = list
                    .filter(u => String(u.id) !== String(meQuery.data?.id))
                    .map(u => ({ id: u.id, label: `${u.name || u.email} (${u.email})` }))
                if (!ignore) {
                    setDeputyCandidateOptions(mapped)
                }
            } catch {
                if (!ignore) setDeputyCandidateOptions([])
            }
        }
        loadDeputies()
        return () => { ignore = true }
    }, [orgId, meQuery.data, isGaPlanner])

    // 조직 변경 시 후보에 없는 대직자는 자동 해제 + 중복 방지
    React.useEffect(() => {
        const isOk = (val) => !val || deputyCandidateOptions.some(o => String(o.id) === String(val))
        if (!isOk(deputyUserId1)) setDeputyUserId1('')
        if (!isOk(deputyUserId2)) setDeputyUserId2('')
        if (deputyUserId1 && deputyUserId2 && String(deputyUserId1) === String(deputyUserId2)) setDeputyUserId2('')
    }, [deputyCandidateOptions, deputyUserId1, deputyUserId2])

    const updateMutation = useMutation({
        mutationFn: async (payload) => {
            const needsDeputy = !isGaPlanner && (payload.availabilityStatus === 'VACATION' || payload.availabilityStatus === 'AWAY' || payload.availabilityStatus === 'TRAINING')
            const ids = [deputyUserId1, deputyUserId2].filter(Boolean).map(String)
            const uniqueIds = Array.from(new Set(ids))
            if (needsDeputy && uniqueIds.length === 0) {
                throw new Error('휴가/부재중일 때는 대직자를 최소 1명 선택해야 합니다')
            }
            await api.put('/users/me', { name: payload.name, phoneNumber: payload.phoneNumber, orgId: payload.orgId })
            if (payload.availabilityStatus) {
                const me = meQuery.data
                const deputyUserIds = uniqueIds.map(n => Number(n))
                const body = {
                    userId: me.id,
                    status: payload.availabilityStatus,
                    startAt: payload.startAt ? payload.startAt : new Date().toISOString(),
                    endAt: payload.endAt || null,
                    deputyUserId: deputyUserIds.length > 0 ? deputyUserIds[0] : null, // 하위호환
                    deputyUserIds,
                }
                await api.post('/manager-status', body)
            }
            const res = await api.get('/users/me')
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        onSuccess: (updated) => {
            localStorage.setItem('auth.user', JSON.stringify(updated))
            qc.invalidateQueries({ queryKey: ['me'] })
            try { window.dispatchEvent(new CustomEvent('availability:changed')); } catch { }
            show('저장 완료', 'success')
        },
        onError: (e) => {
            try { window.dispatchEvent(new CustomEvent('availability:failed')); } catch { }
            const msg = e?.response?.data?.message || e?.message || '저장 실패'
            show(msg, 'error')
        }
    })

    const changePasswordMutation = useMutation({
        mutationFn: async ({ currentPassword, newPassword, newPasswordConfirm }) => {
            const res = await api.post('/users/me/change-password', {
                currentPassword,
                newPassword,
                newPasswordConfirm
            })
            return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        },
        onSuccess: () => {
            setPwOpen(false)
            setPwCurrent('')
            setPwNext('')
            setPwNextConfirm('')
            show('비밀번호가 변경되었습니다', 'success')
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '비밀번호 변경에 실패했습니다'
            show(msg, 'error')
        }
    })

    const triggerNotificationTest = async (kind) => {
        setNotifyLoading(true)
        setNotifyMessage('')
        try {
            const res = await api.post(`/notification-test/${kind}`)
            const data = res?.data?.data ?? res?.data
            setNotifyMessage(data?.title ? `알림 전송 완료: ${data.title}` : '알림 전송 완료')
        } catch (e) {
            const msg = e?.response?.data?.message || e.message || '알림 전송에 실패했습니다.'
            setNotifyMessage(msg)
        } finally {
            setNotifyLoading(false)
        }
    }

    if (meQuery.isLoading) return <Loading />
    if (meQuery.isError) return <InlineError message="내 정보를 불러오지 못했습니다" />
    const me = meQuery.data || {}

    return (
        <div className="max-w-5xl">
            <h1 className="text-xl font-semibold mb-6">내 정보</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 1칼럼: 기본 정보 */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm text-slate-600">이메일</label>
                        <div className="mt-1 text-slate-800">{me.email}</div>
                    </div>
                    <div>
                        <label className="block text-sm text-slate-600">권한</label>
                        <div className="mt-1 text-slate-800 text-xs inline-flex flex-wrap gap-2">
                            {(Array.isArray(me.roles) && me.roles.length > 0 ? me.roles : (Array.isArray(currentUser?.roles) ? currentUser.roles : [])).map((r, idx) => (
                                <span key={`${r}-${idx}`} className="px-2 py-1 rounded border bg-slate-50 border-slate-200 text-slate-700">
                                    {RoleLabels?.[r] || r}
                                </span>
                            ))}
                            {(!Array.isArray(me.roles) || me.roles.length === 0) && (!Array.isArray(currentUser?.roles) || currentUser.roles.length === 0) && (
                                <span className="text-slate-500">-</span>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm text-slate-600">계정 상태</label>
                        <div className="mt-1 text-slate-800 text-xs inline-flex items-center gap-2">
                            <span className={`px-2 py-1 rounded border ${status === 'ACTIVE' ? 'bg-green-50 border-green-200 text-green-700' : status === 'SUSPENDED' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>{status}</span>
                            <span className="text-slate-500">(관리자만 수정 가능)</span>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm text-slate-600">이름</label>
                        <input className="mt-1 w-full rounded border px-3 py-2" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm text-slate-600">전화번호</label>
                        <input className="mt-1 w-full rounded border px-3 py-2" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                    </div>
                    <div>
                        <button type="button" className="rounded border px-4 py-2" onClick={() => setPwOpen(true)}>
                            비밀번호 변경
                        </button>
                    </div>
                </div>

                {/* 2칼럼: 소속 변경 + 근무 상태 기록 */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm text-slate-600">근무 상태</label>
                        <select className="mt-1 w-full rounded border px-3 py-2" value={availability} onChange={(e) => setAvailability(e.target.value)}>
                            <option value="ON_DUTY">활성(근무 중)</option>
                            <option value="VACATION">휴가</option>
                            <option value="TRAINING">교육</option>
                            <option value="AWAY">부재중</option>
                        </select>
                    </div>
                    {!isGaPlanner && (availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && (
                        <div className="rounded border p-4">
                            <h2 className="font-semibold mb-2">대직자 우선순위</h2>
                            <div className="text-xs text-slate-500 mb-3">근무 가능은 ON_DUTY만이며, 1순위가 근무 불가면 2순위로 자동 전환됩니다.</div>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm text-slate-600">대직자 1순위(필수)</label>
                                    <select className={`mt-1 w-full rounded border px-3 py-2 ${!deputyUserId1 ? 'border-red-300' : ''}`} value={deputyUserId1} onChange={(e) => setDeputyUserId1(e.target.value)}>
                                        <option value="">선택</option>
                                        {deputyCandidateOptions.map(o => (<option key={o.id} value={String(o.id)}>{o.label}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm text-slate-600">대직자 2순위(선택)</label>
                                    <select className="mt-1 w-full rounded border px-3 py-2" value={deputyUserId2} onChange={(e) => setDeputyUserId2(e.target.value)}>
                                        <option value="">선택 안 함</option>
                                        {deputyCandidateOptions
                                            .filter(o => String(o.id) !== String(deputyUserId1))
                                            .map(o => (<option key={o.id} value={String(o.id)}>{o.label}</option>))}
                                    </select>
                                </div>
                            </div>
                            {(availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && !deputyUserId1 && (
                                <div className="text-xs text-red-600 mt-2">교육/휴가/부재중 상태에서는 대직자 1순위가 필수입니다.</div>
                            )}
                        </div>
                    )}
                    {isGaPlanner && (availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && (
                        <div className="text-xs text-slate-500">
                            GA 설계사 역할은 설계의뢰 자동 배정 대상이 아니므로 대직자 설정이 제공되지 않습니다.
                        </div>
                    )}

                    <div className="rounded border p-4">
                        <h2 className="font-semibold mb-2">소속 변경</h2>
                        <label className="block text-sm text-slate-600">조직</label>
                        <select className="mt-1 w-full rounded border px-3 py-2" value={orgId} onChange={(e) => {
                            const val = e.target.value
                            if (val === orgId) return
                            setNextOrgId(val)
                            setConfirmOpen(true)
                        }} disabled={!canChangeOrg}>
                            <option value="">선택</option>
                            {orgOptions.map(o => (<option key={o.id} value={o.id}>{o.name} ({o.type})</option>))}
                        </select>
                        {!canChangeOrg && (<p className="text-xs text-slate-500 mt-1">소속 변경은 관리자(지점 리더 이상)만 가능합니다.</p>)}
                        <p className="text-xs text-slate-500 mt-1">변경 저장 시 '저장' 버튼을 눌러야 적용됩니다.</p>
                    </div>

                    {/* 근무 상태 히스토리/종료 */}
                    <AvailabilityHistory />
                </div>
            </div>

            <div className="pt-4 flex flex-wrap gap-2 items-center">
                <button
                    className="rounded bg-brand-navy text-white px-4 py-2 disabled:opacity-60"
                    disabled={updateMutation.isLoading || ((availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && !deputyUserId1)}
                    onClick={() => updateMutation.mutate({
                        name,
                        phoneNumber,
                        availabilityStatus: availability,
                        orgId: canChangeOrg && orgId ? Number(orgId) : undefined,
                        startAt: availStart ? new Date(availStart).toISOString() : undefined,
                        endAt: availEnd ? new Date(availEnd).toISOString() : undefined
                    })}
                >
                    저장
                </button>
                {(availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && !deputyUserId1 && (
                    <span className="text-xs text-red-600">교육/휴가/부재중 시 대직자 1순위를 선택하세요.</span>
                )}
                <button className="rounded border px-4 py-2" onClick={logout}>로그아웃</button>
            </div>

                {canTestNotification && (
                    <div className="mt-8 rounded border p-4 space-y-3">
                        <h2 className="font-semibold">웹알림 테스트 (운영자 전용)</h2>
                        <p className="text-xs text-slate-500">
                            아래 버튼을 누르면 현재 계정으로 SSE 웹알림 / FCM 푸시가 테스트용으로 전송됩니다.
                            알림 센터 UI와 브라우저 네트워크 탭의 <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">/notifications/stream</code> 이벤트를 함께 확인해 보세요.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm disabled:opacity-60"
                                disabled={notifyLoading}
                                onClick={() => triggerNotificationTest('signup')}
                            >
                                가입 신청 알림 테스트
                            </button>
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm disabled:opacity-60"
                                disabled={notifyLoading}
                                onClick={() => triggerNotificationTest('request-created')}
                            >
                                의뢰 생성 알림 테스트
                            </button>
                            <button
                                type="button"
                                className="rounded border px-3 py-2 text-sm disabled:opacity-60"
                                disabled={notifyLoading}
                                onClick={() => triggerNotificationTest('request-assigned')}
                            >
                                의뢰 배정 알림 테스트
                            </button>
                        </div>
                        {notifyMessage && (
                            <div className="text-xs text-slate-600">{notifyMessage}</div>
                        )}
                    </div>
                )}

            {confirmOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
                    <div className="w-full max-w-sm rounded bg-white p-6 space-y-4 text-sm">
                        <div className="text-base font-semibold">소속 변경 확인</div>
                        <div className="text-slate-700">소속을 변경하면 권한/접근 범위가 달라질 수 있습니다. 계속 진행하시겠습니까?</div>
                        <div className="text-xs text-slate-500">현재 소속: {originalOrgId || '-'} → 변경 소속: {nextOrgId || '-'}</div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button className="rounded border px-3 py-2" onClick={() => { setConfirmOpen(false); setNextOrgId('') }}>취소</button>
                            <button className="rounded bg-brand-navy text-white px-3 py-2" onClick={() => { setOrgId(nextOrgId); setConfirmOpen(false) }}>변경</button>
                        </div>
                    </div>
                </div>
            )}

            {pwOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
                    <div className="w-full max-w-sm rounded bg-white p-6 space-y-4 text-sm">
                        <div className="text-base font-semibold">비밀번호 변경</div>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm text-slate-600">기존 비밀번호</label>
                                <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-600">변경 비밀번호</label>
                                <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={pwNext} onChange={(e) => setPwNext(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-600">변경 비밀번호 확인</label>
                                <input type="password" className="mt-1 w-full rounded border px-3 py-2" value={pwNextConfirm} onChange={(e) => setPwNextConfirm(e.target.value)} />
                                {pwNext && pwNextConfirm && pwNext !== pwNextConfirm && (
                                    <div className="text-xs text-red-600 mt-1">변경 비밀번호 확인이 일치하지 않습니다.</div>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button className="rounded border px-3 py-2" onClick={() => {
                                setPwOpen(false)
                                setPwCurrent('')
                                setPwNext('')
                                setPwNextConfirm('')
                            }}>취소</button>
                            <button
                                className="rounded bg-brand-navy text-white px-3 py-2 disabled:opacity-60"
                                disabled={changePasswordMutation.isLoading || !pwCurrent || !pwNext || !pwNextConfirm || pwNext !== pwNextConfirm || pwCurrent === pwNext}
                                onClick={() => changePasswordMutation.mutate({ currentPassword: pwCurrent, newPassword: pwNext, newPasswordConfirm: pwNextConfirm })}
                            >
                                {changePasswordMutation.isLoading ? '변경 중…' : '변경'}
                            </button>
                        </div>
                        {pwCurrent && pwNext && pwCurrent === pwNext && (
                            <div className="text-xs text-red-600">기존 비밀번호와 다른 비밀번호로 변경해 주세요.</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}


function AvailabilityHistory() {
    const [items, setItems] = React.useState([])
    const [loading, setLoading] = React.useState(false)
    const [ending, setEnding] = React.useState(false)

    React.useEffect(() => {
        let ignore = false
        async function load() {
            try {
                setLoading(true)
                const res = await api.get('/manager-status/me/history')
                const data = res?.data?.data ?? res?.data ?? []
                if (!ignore) setItems(Array.isArray(data) ? data : [])
            } finally {
                if (!ignore) setLoading(false)
            }
        }
        load()
        return () => { ignore = true }
    }, [])

    const endLatest = async () => {
        setEnding(true)
        try {
            await api.patch('/manager-status/me/end-latest')
            const res = await api.get('/manager-status/me/history')
            const data = res?.data?.data ?? res?.data ?? []
            setItems(Array.isArray(data) ? data : [])
        } finally {
            setEnding(false)
        }
    }

    return (
        <div className="mt-6 rounded border p-4">
            <div className="flex items-center justify-between mb-2">
                <h2 className="font-semibold">근무 상태 기록</h2>
                <button className="rounded border px-3 py-2 text-sm" onClick={endLatest} disabled={ending}>{ending ? '종료 중…' : '현재 근무 종료'}</button>
            </div>
            {loading ? (<Loading />) : (
                <ul className="text-sm divide-y">
                    {items.length === 0 ? (
                        <li className="text-slate-500 py-2">기록이 없습니다</li>
                    ) : (
                        items.map((it, idx) => (
                            <li key={it.id || idx} className="py-2 flex items-start justify-between gap-3">
                                <div>
                                    <div className="font-medium">{it.status}</div>
                                    <div className="text-xs text-slate-600">{it.startAt} → {it.endAt || '진행중'}</div>
                                    {it.deputyUserId && (<div className="text-xs text-slate-500">대직자 ID: {it.deputyUserId}</div>)}
                                </div>
                                {it.endAt && (
                                    <button className="text-xs text-red-600 hover:underline" onClick={async () => {
                                        if (!window.confirm('이 기록을 삭제하시겠습니까?')) return
                                        try {
                                            await api.delete(`/manager-status/${it.id}`)
                                            const res = await api.get('/manager-status/me/history')
                                            const data = res?.data?.data ?? res?.data ?? []
                                            setItems(Array.isArray(data) ? data : [])
                                        } catch { }
                                    }}>삭제</button>
                                )}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    )
}


